import React from 'react';
import PropTypes from 'prop-types';

import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Tooltip,
  Typography,
  HelpCircleIcon,
  Link01Icon,
  ChevronDownIcon,
} from '@metarouter-private/mr-mui';

import CopyToClipboard from 'app/components/CopyToClipboard';

export default function EndpointUrlsAccordion({ endpointUrls }) {
  if (!endpointUrls || endpointUrls.length === 0) {
    return null;
  }

  // sx required — Accordion doesn't expose props for per-corner border-radius
  // or the ::before pseudo-element that MUI renders as a divider line.
  return (
    <Accordion
      variant="outlined"
      disableGutters
      sx={{
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
        '&::before': {
          display: 'none',
        },
      }}
    >
      <AccordionSummary expandIcon={<ChevronDownIcon />}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Link01Icon fontSize="small" />
          <Typography variant="body2">Endpoint URLs</Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={1.5}>
          {endpointUrls.map(({ label, url, description }) => (
            <EndpointUrlRow key={label} label={label} url={url} description={description} />
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

export function EndpointUrlRow({ label, url, description }) {
  return (
    <Stack spacing={0.5}>
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        {description && (
          <Tooltip title={description} arrow placement="top">
            <span>
              <HelpCircleIcon fontSize="small" color="secondary" />
            </span>
          </Tooltip>
        )}
      </Stack>
      {/* sx required — Stack/Typography don't expose props for minWidth, flex,
          or userSelect needed for the truncation + copy pattern. */}
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
        <Typography
          variant="body2"
          noWrap
          title={url}
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            userSelect: 'text',
          }}
        >
          {url}
        </Typography>
        <CopyToClipboard text={url} />
      </Stack>
    </Stack>
  );
}

EndpointUrlsAccordion.propTypes = {
  endpointUrls: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      description: PropTypes.string,
    })
  ),
};

EndpointUrlRow.propTypes = {
  label: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  description: PropTypes.string,
};
